import { defineComponent as _defineComponent } from 'vue'
import { computed, getCurrentInstance, onBeforeUnmount, onMounted, ref, watch } from "vue";
import { useTranslations } from "@/hooks/useTranslations";
import type { Activity } from "@/models/wellness/Activity";
import type { SelectedActivityInput } from "@/models/wellness/SelectedActivityInput";
import type { WellnessCalculationRequest } from "@/models/wellness/WellnessCalculationRequest";
import type { WellnessActivityType } from "@/models/wellness/WellnessActivityType";
import {
  listWellnessActivities,
  listWellnessActivityTypes,
} from "../../services/wellnessApi";
import { sortActivityTypes } from "./activityOrdering";


export default /*#__PURE__*/_defineComponent({
  __name: 'UserInputCard',
  props: {
    generateDisabled: { type: Boolean },
    hasZeroCalories: { type: Boolean }
  },
  emits: ["calculate"],
  setup(__props: any, { emit }) {

const props = __props as {
  generateDisabled?: boolean;
  hasZeroCalories?: boolean;
};

const DEFAULT_WHEEL_CIRCUMFERENCE_METERS = "2.1";
const SPEED_BOUNDARY_OFFSET = 0.5;
const PERSONAL_DEFAULTS = {
  male: {
    age: 30,
    heightCm: 175,
    weightKg: 70,
  },
  female: {
    age: 30,
    heightCm: 162,
    weightKg: 60,
  },
} as const;





const vm = getCurrentInstance();
const translations = useTranslations();
const unitSystem = ref<"metric" | "imperial">("metric");
const height = ref("");
const weight = ref("");
const age = ref("");
const sex = ref<"male" | "female">("male");
const showAdvancedOptions = ref(false);
const selectedActivities = ref<number[]>([]);
const selectedCatalogTypes = ref<number[]>([]);
const expandedType = ref<number | null>(null);
const activitySearchQuery = ref("");
const activityTypes = ref<WellnessActivityType[]>([]);
const activitiesByTypeMap = ref<Record<number, Activity[]>>({});
const speedInputs = ref<Record<number, string>>({});
const wheelCircumferenceInputs = ref<Record<number, string>>({});
const loadingTypes = ref(false);
const loadingActivities = ref<Record<number, boolean>>({});
const formError = ref("");
const showPersonalInfoValidation = ref(false);
const showActivityTypeValidation = ref(false);
const showActivitySelectionValidation = ref(false);

const hasActivitySelections = computed(
  () => selectedActivities.value.length > 0,
);

const orderedActivityTypes = computed(() => {
  return sortActivityTypes(activityTypes.value);
});

const orderedSelectedCatalogTypes = computed(() => {
  const selectedTypeIds = new Set(selectedCatalogTypes.value);
  return orderedActivityTypes.value
    .filter((type) => selectedTypeIds.has(type.id))
    .map((type) => type.id);
});

onMounted(async () => {
  document.addEventListener("click", handleDocumentClick);
  document.addEventListener("keydown", handleDocumentKeydown);
  loadingTypes.value = true;
  try {
    activityTypes.value = await listWellnessActivityTypes();
  } catch (error: any) {
    showError(error);
  } finally {
    loadingTypes.value = false;
  }
});

onBeforeUnmount(() => {
  document.removeEventListener("click", handleDocumentClick);
  document.removeEventListener("keydown", handleDocumentKeydown);
});

watch(
  sex,
  () => {
    applyPersonalDefaults();
  },
  { immediate: true },
);

watch(unitSystem, (nextUnit, previousUnit) => {
  if (!previousUnit || nextUnit === previousUnit) {
    return;
  }

  const currentHeight = Number.parseFloat(height.value);
  const currentWeight = Number.parseFloat(weight.value);

  if (!Number.isNaN(currentHeight)) {
    height.value = nextUnit === "imperial"
      ? `${Math.round(currentHeight / 2.54)}`
      : `${Math.round(currentHeight * 2.54)}`;
  }

  if (!Number.isNaN(currentWeight)) {
    weight.value = nextUnit === "imperial"
      ? `${Math.round(currentWeight / 0.453592)}`
      : `${Math.round(currentWeight * 0.453592)}`;
  }
});

watch(showAdvancedOptions, async (enabled) => {
  formError.value = "";
  showPersonalInfoValidation.value = false;
  showActivitySelectionValidation.value = false;
  closeDropdown();

  if (!enabled) {
    return;
  }

  for (const typeId of selectedCatalogTypes.value) {
    await ensureActivitiesLoaded(typeId);
    applyDefaultActivitySelection(typeId);
  }
});

function applyPersonalDefaults() {
  const defaults = PERSONAL_DEFAULTS[sex.value];
  age.value = `${defaults.age}`;
  height.value = unitSystem.value === "imperial"
    ? `${Math.round(defaults.heightCm / 2.54)}`
    : `${defaults.heightCm}`;
  weight.value = unitSystem.value === "imperial"
    ? `${Math.round(defaults.weightKg / 0.453592)}`
    : `${defaults.weightKg}`;
}

function applyDefaultActivitySelection(typeId: number) {
  const hasSelection = selectedActivitiesByType(typeId).length > 0;
  if (hasSelection) {
    return;
  }

  const defaultActivity = visibleActivitiesForType(typeId).find(
    (activity) => activity.isDefault,
  );

  if (defaultActivity && !selectedActivities.value.includes(defaultActivity.id)) {
    toggleActivity(defaultActivity.id);
  }
}

async function ensureActivitiesLoaded(typeId: number) {
  if (activitiesByTypeMap.value[typeId]) {
    return;
  }

  loadingActivities.value = {
    ...loadingActivities.value,
    [typeId]: true,
  };

  try {
    const activities = await listWellnessActivities(typeId);
    activitiesByTypeMap.value = {
      ...activitiesByTypeMap.value,
      [typeId]: activities,
    };
  } catch (error: any) {
    showError(error);
  } finally {
    loadingActivities.value = {
      ...loadingActivities.value,
      [typeId]: false,
    };
  }
}

async function toggleCatalogType(typeId: number) {
  await ensureActivitiesLoaded(typeId);

  const typeActivities = visibleActivitiesForType(typeId);
  const typeIds = new Set(typeActivities.map((activity) => activity.id));

  if (selectedCatalogTypes.value.includes(typeId)) {
    selectedCatalogTypes.value = selectedCatalogTypes.value.filter(
      (item) => item !== typeId,
    );
    selectedActivities.value = selectedActivities.value.filter(
      (id) => !typeIds.has(id),
    );

    typeActivities.forEach(clearActivityInputs);

    if (expandedType.value === typeId) {
      expandedType.value = null;
      activitySearchQuery.value = "";
    }
    return;
  }

  selectedCatalogTypes.value = [...selectedCatalogTypes.value, typeId];

  if (showAdvancedOptions.value) {
    applyDefaultActivitySelection(typeId);
  }
}

async function toggleTypeDropdown(typeId: number) {
  if (expandedType.value === typeId) {
    closeDropdown();
    return;
  }

  await ensureActivitiesLoaded(typeId);
  expandedType.value = typeId;
  activitySearchQuery.value = "";
}

function closeDropdown() {
  expandedType.value = null;
  activitySearchQuery.value = "";
}

function handleDocumentClick(event: MouseEvent) {
  const target = event.target as HTMLElement | null;
  if (!target?.closest(".activity-dropdown-shell")) {
    closeDropdown();
  }
}

function handleDocumentKeydown(event: KeyboardEvent) {
  if (event.key === "Escape") {
    closeDropdown();
  }
}

function activitiesForType(typeId: number) {
  return activitiesByTypeMap.value[typeId] || [];
}

function visibleActivitiesForType(typeId: number) {
  // Hide activities that do not define speed input, per Wellness selection rules.
  return activitiesForType(typeId).filter(
    (activity) => activity.speedInputMode !== "None",
  );
}

function getActivityLabel(activity: Activity) {
  const label = activity.description?.trim() || activity.speedDisplayText?.trim();
  return label || `Activity ${activity.id}`;
}

function filteredActivitiesByType(typeId: number) {
  const query = activitySearchQuery.value.trim().toLowerCase();
  const typeActivities = visibleActivitiesForType(typeId);

  if (!query) {
    return typeActivities;
  }

  return typeActivities.filter((activity) => {
    return (
      getActivityLabel(activity).toLowerCase().includes(query) ||
      activity.activityCode.toLowerCase().includes(query)
    );
  });
}

function selectedActivitiesByType(typeId: number) {
  const typeIds = new Set(
    visibleActivitiesForType(typeId).map((activity) => activity.id),
  );

  return visibleActivitiesForType(typeId).filter(
    (activity) =>
      typeIds.has(activity.id) &&
      selectedActivities.value.includes(activity.id),
  );
}

function selectedActivityList() {
  return selectedCatalogTypes.value.flatMap((typeId) =>
    selectedActivitiesByType(typeId),
  );
}

function activitiesForCalculation() {
  if (!showAdvancedOptions.value) {
    return selectedCatalogTypes.value.flatMap((typeId) =>
      visibleActivitiesForType(typeId).slice(0, 1),
    );
  }

  return selectedActivityList();
}

function activityCountByType(typeId: number) {
  return selectedActivitiesByType(typeId).length;
}

function getTypeName(typeId: number) {
  return (
    activityTypes.value.find((type) => type.id === typeId)?.name ||
    translations.value.get("wellness_activity_default_name")
  );
}

function selectedLabelByType(typeId: number) {
  const selectedForType = selectedActivitiesByType(typeId);
  const typeName = getTypeName(typeId);

  if (selectedForType.length === 0) {
    return translations.value.get("wellness_select_activities_placeholder", typeName.toLowerCase());
  }

  if (selectedForType.length === 1) {
    return getActivityLabel(selectedForType[0]);
  }

  return translations.value.get("wellness_selected_count", selectedForType.length);
}

function toggleActivity(id: number) {
  const activity = selectedActivityList()
    .concat(Object.values(activitiesByTypeMap.value).flat())
    .find((item) => item.id === id);
  const isSelected = selectedActivities.value.includes(id);

  if (isSelected) {
    selectedActivities.value = selectedActivities.value.filter(
      (activityId) => activityId !== id,
    );
    clearActivityInputsById(id);
    return;
  }

  selectedActivities.value = [...selectedActivities.value, id];

  if (!activity) {
    return;
  }

  const defaultSpeedValue = getDefaultSpeedValue(activity);
  if (defaultSpeedValue) {
    speedInputs.value[activity.id] = defaultSpeedValue;
  }

  if (requiresWheelCircumference(activity)) {
    wheelCircumferenceInputs.value[activity.id] =
      wheelCircumferenceInputs.value[activity.id] ||
      DEFAULT_WHEEL_CIRCUMFERENCE_METERS;
  }
}

function clearActivityInputs(activity: Activity) {
  clearActivityInputsById(activity.id);
}

function clearActivityInputsById(activityId: number) {
  delete speedInputs.value[activityId];
  delete wheelCircumferenceInputs.value[activityId];
}

function hasSpeedMetadata(activity: Activity) {
  return (
    activity.exactMph != null ||
    activity.minMph != null ||
    activity.maxMph != null ||
    (activity.speedInputMode != null && activity.speedInputMode !== "None")
  );
}

function requiresSpeedInput(activity: Activity) {
  return hasSpeedMetadata(activity) || requiresCadence(activity);
}

function requiresWheelCircumference(activity: Activity) {
  return (
    activity.bicyclingComputationApproach === "WheelCircumferenceRevolutions"
  );
}

function requiresCadence(activity: Activity) {
  return activity.bicyclingComputationApproach === "CadenceRpm";
}

function speedHint(activity: Activity) {
  if (requiresCadence(activity)) {
    return cadenceHint();
  }

  if (activity.speedInputMode === "Exact" && activity.exactMph != null) {
    return translations.value.get("wellness_speed_hint_exact", activity.exactMph);
  }

  if (
    activity.speedInputMode === "Range" &&
    activity.minMph != null &&
    activity.maxMph != null
  ) {
    return translations.value.get("wellness_speed_hint_range", activity.minMph, activity.maxMph);
  }

  if (activity.speedInputMode === "MaxOnly" && activity.maxMph != null) {
    return translations.value.get("wellness_speed_hint_max", activity.maxMph);
  }

  if (activity.speedInputMode === "MinOnly" && activity.minMph != null) {
    return translations.value.get("wellness_speed_hint_min", activity.minMph);
  }

  if (activity.speedDisplayText?.trim()) {
    return activity.speedDisplayText;
  }

  return translations.value.get("wellness_speed_hint_manual");
}

function speedInputStep() {
  return 0.01;
}

function getDerivedSpeedValue(activity: Activity) {
  if (activity.exactMph != null) {
    return activity.exactMph;
  }

  if (activity.minMph != null && activity.maxMph != null) {
    return (activity.minMph + activity.maxMph) / 2;
  }

  if (activity.minMph != null) {
    return activity.minMph;
  }

  if (activity.maxMph != null) {
    return activity.maxMph / 2;
  }

  return undefined;
}

function getSpeedInputMin(activity: Activity) {
  const step = speedInputStep();

  if (activity.speedInputMode === "Exact" && activity.exactMph != null) {
    return activity.exactMph;
  }

  if (activity.speedInputMode === "Range" && activity.minMph != null) {
    return activity.minMph;
  }

  if (activity.speedInputMode === "MinOnly" && activity.minMph != null) {
    return activity.minMph + step;
  }

  return undefined;
}

function getSpeedInputMax(activity: Activity) {
  const step = speedInputStep();

  if (activity.speedInputMode === "Exact" && activity.exactMph != null) {
    return activity.exactMph;
  }

  if (activity.speedInputMode === "Range" && activity.maxMph != null) {
    return activity.maxMph;
  }

  if (activity.speedInputMode === "MaxOnly" && activity.maxMph != null) {
    return activity.maxMph - step;
  }

  return undefined;
}

function isSpeedReadOnly(activity: Activity) {
  return activity.speedInputMode === "Exact" && activity.exactMph != null;
}

function getDefaultSpeedValue(activity: Activity) {
  const derivedSpeedValue = getDerivedSpeedValue(activity);
  if (derivedSpeedValue != null) {
    return derivedSpeedValue.toFixed(2);
  }

  return "";
}

function normalizeSpeedInput(activity: Activity) {
  const rawValue = speedInputs.value[activity.id]?.trim();
  if (!rawValue) {
    return;
  }

  const parsedValue = Number.parseFloat(rawValue);
  if (Number.isNaN(parsedValue)) {
    return;
  }

  const min = getSpeedInputMin(activity);
  const max = getSpeedInputMax(activity);
  let normalizedValue = parsedValue;

  if (min != null && normalizedValue < min) {
    normalizedValue = min;
  }

  if (max != null && normalizedValue > max) {
    normalizedValue = max;
  }

  if (activity.speedInputMode === "Exact" && activity.exactMph != null) {
    normalizedValue = activity.exactMph;
  }

  speedInputs.value[activity.id] = normalizedValue.toFixed(2);
}

function handleSpeedInput(activity: Activity, event: Event) {
  const target = event.target as HTMLInputElement | null;
  if (!target) {
    return;
  }

  const rawValue = target.value;
  if (!rawValue) {
    speedInputs.value[activity.id] = "";
    return;
  }

  if (activity.speedInputMode === "Exact" && activity.exactMph != null) {
    const exactValue = activity.exactMph.toFixed(2);
    speedInputs.value[activity.id] = exactValue;
    target.value = exactValue;
    return;
  }

  speedInputs.value[activity.id] = rawValue;

  const parsedValue = Number.parseFloat(rawValue);
  if (Number.isNaN(parsedValue)) {
    return;
  }

  const max = getSpeedInputMax(activity);
  if (max == null || parsedValue <= max) {
    return;
  }

  const formattedValue = max.toFixed(2);
  speedInputs.value[activity.id] = formattedValue;
  target.value = formattedValue;
}

function wheelHint() {
  return translations.value.get("wellness_wheel_hint");
}

function cadenceHint() {
  return translations.value.get("wellness_cadence_hint");
}

function hasValidPositiveNumber(value: string) {
  const parsedValue = Number.parseFloat(value);
  return !Number.isNaN(parsedValue) && parsedValue > 0;
}

function hasValidAge(value: string) {
  const parsedValue = Number.parseInt(value, 10);
  return !Number.isNaN(parsedValue) && parsedValue > 0;
}

const personalInfoFieldErrors = computed(() => ({
  height: showPersonalInfoValidation.value && !hasValidPositiveNumber(height.value),
  weight: showPersonalInfoValidation.value && !hasValidPositiveNumber(weight.value),
  age: showPersonalInfoValidation.value && !hasValidAge(age.value),
}));

const activityTypeSelectionInvalid = computed(() =>
  showActivityTypeValidation.value && selectedCatalogTypes.value.length === 0,
);

const activitySelectionInvalid = computed(() =>
  showActivitySelectionValidation.value &&
  showAdvancedOptions.value &&
  selectedCatalogTypes.value.length > 0 &&
  selectedActivityList().length === 0,
);

function buildSelectedActivityInputs(
  selected: Activity[],
): SelectedActivityInput[] {
  return selected.map((activity) => {
    const speedText = speedInputs.value[activity.id]?.trim() || getDefaultSpeedValue(activity);
    const wheelText = wheelCircumferenceInputs.value[activity.id]?.trim();
    const parsedSpeedValue = speedText ? Number.parseFloat(speedText) : undefined;
    const parsedWheelValue = wheelText
      ? Number.parseFloat(wheelText)
      : requiresWheelCircumference(activity)
        ? Number.parseFloat(DEFAULT_WHEEL_CIRCUMFERENCE_METERS)
        : undefined;

    return {
      activityId: activity.id,
      enteredSpeedMph: requiresCadence(activity)
        ? undefined
        : adjustSpeedValueForRequest(activity, parsedSpeedValue),
      wheelCircumferenceMeters: parsedWheelValue,
      cadenceRpm: requiresCadence(activity) ? parsedSpeedValue : undefined,
    };
  });
}

function adjustSpeedValueForRequest(
  activity: Activity,
  speedValue?: number,
) {
  if (speedValue == null || Number.isNaN(speedValue)) {
    return speedValue;
  }

  if (
    activity.speedInputMode === "MinOnly" &&
    activity.minMph != null &&
    Math.abs(speedValue - activity.minMph) < 0.01
  ) {
    return activity.minMph + SPEED_BOUNDARY_OFFSET;
  }

  if (
    activity.speedInputMode === "MaxOnly" &&
    activity.maxMph != null &&
    Math.abs(speedValue - activity.maxMph) < 0.01
  ) {
    return activity.maxMph - SPEED_BOUNDARY_OFFSET;
  }

  return speedValue;
}

function validateSelectedActivities(selected: Activity[]): boolean {
  for (const activity of selected) {
    const speedError = validateSpeedInput(activity);
    if (speedError) {
      formError.value = speedError;
      return false;
    }

    const wheelError = validateWheelInput(activity);
    if (wheelError) {
      formError.value = wheelError;
      return false;
    }

    const cadenceError = validateCadenceInput(activity);
    if (cadenceError) {
      formError.value = cadenceError;
      return false;
    }
  }

  return true;
}

function validateSpeedInput(activity: Activity) {
  const speedText = speedInputs.value[activity.id]?.trim() || getDefaultSpeedValue(activity);

  if (requiresSpeedInput(activity) && !speedText) {
    if (requiresCadence(activity)) {
      return translations.value.get("wellness_validation_cadence_required", getActivityLabel(activity));
    }

    return translations.value.get("wellness_validation_speed_required", getActivityLabel(activity));
  }

  if (!speedText) {
    return "";
  }

  const speedValue = Number.parseFloat(speedText);
  if (Number.isNaN(speedValue) || speedValue <= 0) {
    if (requiresCadence(activity)) {
      return translations.value.get("wellness_validation_cadence_invalid", getActivityLabel(activity));
    }

    return translations.value.get("wellness_validation_speed_invalid", getActivityLabel(activity));
  }

  if (
    activity.speedInputMode === "Exact" &&
    activity.exactMph != null &&
    Math.abs(speedValue - activity.exactMph) > 0.01
  ) {
    return translations.value.get("wellness_validation_speed_exact", getActivityLabel(activity), activity.exactMph);
  }

  if (
    activity.speedInputMode === "Range" &&
    ((activity.minMph != null && speedValue < activity.minMph) ||
      (activity.maxMph != null && speedValue > activity.maxMph))
  ) {
    return translations.value.get("wellness_validation_speed_range", getActivityLabel(activity), activity.minMph, activity.maxMph);
  }

  if (
    activity.speedInputMode === "MaxOnly" &&
    activity.maxMph != null &&
    speedValue >= activity.maxMph
  ) {
    return translations.value.get("wellness_validation_speed_max", getActivityLabel(activity), activity.maxMph);
  }

  if (
    activity.speedInputMode === "MinOnly" &&
    activity.minMph != null &&
    speedValue < activity.minMph
  ) {
    return translations.value.get("wellness_validation_speed_min", getActivityLabel(activity), activity.minMph);
  }

  return "";
}

function validateWheelInput(activity: Activity) {
  if (!requiresWheelCircumference(activity)) {
    return "";
  }

  const wheelText = wheelCircumferenceInputs.value[activity.id]?.trim();
  if (!wheelText) {
    return translations.value.get("wellness_validation_wheel_required", getActivityLabel(activity));
  }

  const wheelValue = Number.parseFloat(wheelText);
  if (Number.isNaN(wheelValue) || wheelValue <= 0) {
    return translations.value.get("wellness_validation_wheel_invalid", getActivityLabel(activity));
  }

  return "";
}

function validateCadenceInput(activity: Activity) {
  return "";
}

function handleCalculate() {
  formError.value = "";
  showPersonalInfoValidation.value = false;
  showActivityTypeValidation.value = false;
  showActivitySelectionValidation.value = false;

  if (props.hasZeroCalories) {
    formError.value =
      translations.value.get("wellness_validation_zero_calories");
    return;
  }

  if (props.generateDisabled) {
    formError.value = translations.value.get("wellness_validation_calories_loading");
    return;
  }

  const weightKg =
    unitSystem.value === "imperial"
      ? Number.parseFloat(weight.value) * 0.453592
      : Number.parseFloat(weight.value);
  const heightCm =
    unitSystem.value === "imperial"
      ? Number.parseFloat(height.value) * 2.54
      : Number.parseFloat(height.value);

  if (showAdvancedOptions.value && (!weightKg || !heightCm || !Number.parseInt(age.value, 10))) {
    showPersonalInfoValidation.value = true;
    formError.value = translations.value.get("wellness_validation_enter_personal_info");
    return;
  }

  const selected =
    selectedCatalogTypes.value.length > 0 ? activitiesForCalculation() : [];

  if (!showAdvancedOptions.value && selectedCatalogTypes.value.length === 0) {
    showActivityTypeValidation.value = true;
    formError.value = translations.value.get("wellness_validation_select_activity");
    return;
  }

  if (showAdvancedOptions.value && selected.length === 0) {
    if (selectedCatalogTypes.value.length === 0) {
      showActivityTypeValidation.value = true;
    } else {
      showActivitySelectionValidation.value = true;
    }
    formError.value = translations.value.get("wellness_validation_select_activity");
    return;
  }

  if (showAdvancedOptions.value && !validateSelectedActivities(selected)) {
    return;
  }

  const requestPayload: Omit<WellnessCalculationRequest, "caloriesTarget"> = {
    heightCm,
    weightKg,
    age: Number.parseInt(age.value, 10),
    sex: sex.value,
    activities: buildSelectedActivityInputs(selected),
  };

  emit("calculate", {
    ...requestPayload,
    mode: showAdvancedOptions.value ? "advanced" : "general",
    selectedActivityTypeIds: [...selectedCatalogTypes.value],
  });
}

function showError(error: any) {
  const message =
    error?.response?.data?.errorMessages?.[0]?.userVisibleMessage ||
    error?.response?.data ||
    "Network error";
  vm?.proxy?.$snotify?.error(message);
}

return { __sfc: true,DEFAULT_WHEEL_CIRCUMFERENCE_METERS, SPEED_BOUNDARY_OFFSET, PERSONAL_DEFAULTS, props, emit, vm, translations, unitSystem, height, weight, age, sex, showAdvancedOptions, selectedActivities, selectedCatalogTypes, expandedType, activitySearchQuery, activityTypes, activitiesByTypeMap, speedInputs, wheelCircumferenceInputs, loadingTypes, loadingActivities, formError, showPersonalInfoValidation, showActivityTypeValidation, showActivitySelectionValidation, hasActivitySelections, orderedActivityTypes, orderedSelectedCatalogTypes, applyPersonalDefaults, applyDefaultActivitySelection, ensureActivitiesLoaded, toggleCatalogType, toggleTypeDropdown, closeDropdown, handleDocumentClick, handleDocumentKeydown, activitiesForType, visibleActivitiesForType, getActivityLabel, filteredActivitiesByType, selectedActivitiesByType, selectedActivityList, activitiesForCalculation, activityCountByType, getTypeName, selectedLabelByType, toggleActivity, clearActivityInputs, clearActivityInputsById, hasSpeedMetadata, requiresSpeedInput, requiresWheelCircumference, requiresCadence, speedHint, speedInputStep, getDerivedSpeedValue, getSpeedInputMin, getSpeedInputMax, isSpeedReadOnly, getDefaultSpeedValue, normalizeSpeedInput, handleSpeedInput, wheelHint, cadenceHint, hasValidPositiveNumber, hasValidAge, personalInfoFieldErrors, activityTypeSelectionInvalid, activitySelectionInvalid, buildSelectedActivityInputs, adjustSpeedValueForRequest, validateSelectedActivities, validateSpeedInput, validateWheelInput, validateCadenceInput, handleCalculate, showError }
}

})